/**
 * Language switcher component for changing the app language.
 * Supports optional auto-sync to user profile when authenticated.
 * Only shows languages that are in the tenant's allowedLanguages list (EL-694).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { LANGUAGES, type Language } from '../i18n/types';
import { getLanguageName } from '../i18n/utils';

/**
 * Props for the LanguageSwitcher component.
 */
interface LanguageSwitcherProps {
  /**
   * Whether the user is authenticated.
   * When true, language changes will be synced to the user's profile.
   * @default false
   */
  isAuthenticated?: boolean;

  /**
   * Variant for different use cases.
   * - 'default': Full language names (English, Nederlands, Français)
   * - 'compact': Short codes (EN, NL, FR)
   * @default 'default'
   */
  variant?: 'default' | 'compact';

  /**
   * Languages allowed by the tenant (EL-694).
   * When provided, only these languages are offered. Falls back to all
   * platform languages when absent (e.g. pre-login / unauthenticated context).
   */
  allowedLanguages?: string[];
}

/**
 * Language switcher component that allows users to change the app language.
 *
 * Features:
 * - Displays all available languages (English, Dutch, French)
 * - Shows current active language
 * - Handles language change with loading state
 * - Auto-syncs to user profile when authenticated
 *
 * @example
 * ```tsx
 * // On login screen (no sync)
 * <LanguageSwitcher />
 *
 * // In authenticated app (with sync)
 * <LanguageSwitcher isAuthenticated />
 *
 * // Compact variant for tight spaces
 * <LanguageSwitcher isAuthenticated variant="compact" />
 * ```
 */
export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  isAuthenticated = false,
  variant = 'default',
  allowedLanguages,
}) => {
  const { t } = useTranslation('common');
  const {
    language: currentLanguage,
    changeLanguage,
    isChanging,
    isSyncing,
  } = useLanguage({
    syncToProfile: isAuthenticated,
    onSyncError: (error) => {
      console.error('Failed to sync language to profile:', error);
    },
  });

  // When the tenant restricts languages, only show the allowed subset (EL-694).
  // Filter against the platform-supported Language type for type safety.
  // No fallback to the full list on an empty intersection — that would
  // silently defeat the tenant's restriction instead of just showing fewer
  // options.
  const languages = allowedLanguages
    ? LANGUAGES.filter(lang => allowedLanguages.includes(lang))
    : LANGUAGES;

  const handleLanguageChange = async (newLanguage: Language) => {
    try {
      await changeLanguage(newLanguage);
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  const getButtonLabel = (lang: Language): string => {
    if (variant === 'compact') {
      return lang.toUpperCase();
    }
    return getLanguageName(lang);
  };

  return (
    <div className="flex gap-2 items-center">
      {languages.map((lang) => {
        const isActive = lang === currentLanguage;
        const isDisabled = isChanging || isActive;

        return (
          <button
            key={lang}
            onClick={() => handleLanguageChange(lang)}
            disabled={isDisabled}
            className={`
              ${variant === 'compact' ? 'py-1.5 px-3 text-xs' : 'py-2 px-4 text-sm'}
              border border-primary rounded
              transition-all duration-200 ease-in-out
              ${isActive
                ? 'bg-primary text-primary-foreground font-bold opacity-60 cursor-not-allowed'
                : 'bg-transparent text-primary font-normal cursor-pointer hover:bg-primary/10'
              }
              ${isDisabled && !isActive ? 'opacity-60 cursor-not-allowed' : ''}
            `}
            aria-label={`Switch to ${getLanguageName(lang)}`}
            aria-current={isActive ? 'true' : 'false'}
          >
            {getButtonLabel(lang)}
          </button>
        );
      })}
      {(isChanging || isSyncing) && (
        <span className="text-xs text-muted-foreground">
          {isSyncing ? '...' : t('loading', 'Loading...')}
        </span>
      )}
    </div>
  );
};
